import {CircularProgress, Divider, Link, List, ListItem, ListItemText} from '@material-ui/core';
import {getNews} from 'Agent/AgentInformation/ducks/news';
import {NewsEntry} from 'Agent/AgentInformation/models/NewsEntry';
import {Page} from 'Core/models/Page';
import * as A from 'fp-ts/lib/Array';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';

type AgentNewsListProps = {
  newsPage: Page<NewsEntry>;
};
const renderLink = (encodedLinkToParse: string): JSX.Element => {
  const linkTextPattern = /#Link\|(.*)\|(.*)#/;
  const [fullMatch, linkText, linkHref] = encodedLinkToParse.match(linkTextPattern) || [];

  return linkText && linkHref ? <Link href={linkHref}>{linkText}</Link> : <em>Unable to render link.</em>;
};

const convertNewlinesToHtmlBreaks = (stringToFormat: string) =>
  pipe(
    stringToFormat.split('\n'),
    A.map(stringSplitByNewline => <>{stringSplitByNewline}</>),
    NEA.fromArray,
    O.fold(
      () => <>{''}</>,
      stringsSplitByNewlines => (
        <>
          {NEA.head(stringsSplitByNewlines)}
          {NEA.tail(stringsSplitByNewlines).map(stringSplitByNewline => (
            <>
              <br />
              {stringSplitByNewline}
            </>
          ))}
        </>
      )
    )
  );

const formatDescription = (content: string) => {
  const linkPattern = /#Link\|.*\|.*#/g;
  const articleText = content.split(linkPattern);
  const links = content.match(linkPattern) || [];

  const description = A.zipWith(articleText, links, (textEntry, link) => {
    const formattedArticleText = convertNewlinesToHtmlBreaks(textEntry);

    return (
      <>
        {formattedArticleText}
        {renderLink(link)}
      </>
    );
  });

  const finalArticleText = pipe(
    A.last(articleText),
    O.getOrElse(() => '')
  );

  return description.length > 0 ? (
    <>
      {description}
      {finalArticleText}
    </>
  ) : (
    convertNewlinesToHtmlBreaks(content)
  );
};

const AgentNewsList = (props: AgentNewsListProps) => {
  const {newsPage} = props;
  const listRef = useRef<HTMLUListElement | null>(null);
  const [hasRequestedAdditionalNews, setHasRequestedAdditionalNews] = useState(false);
  const dispatch = useDispatch();
  const getHasScrolledPastThreshold = () => {
    const thresholdToLoadMore = 200;
    const currentScrollPosition = listRef.current?.scrollTop || 0;
    const offsetScrollHeight = listRef.current?.offsetHeight || 0;
    const totalScrollHeight = listRef.current?.scrollHeight || 0;
    const currentScrollPositionFromBottom = totalScrollHeight - offsetScrollHeight - currentScrollPosition;

    return currentScrollPositionFromBottom <= thresholdToLoadMore;
  };
  const hasScrolledPastThreshold = getHasScrolledPastThreshold();
  const hasAdditionalNewsItemsToLoad = newsPage.result.length < newsPage.totalCount;
  const currentResultCount = newsPage.result.length;

  useEffect(() => {
    if (hasScrolledPastThreshold && hasAdditionalNewsItemsToLoad) {
      dispatch(getNews(newsPage));
    }
  }, [hasScrolledPastThreshold, hasAdditionalNewsItemsToLoad]);

  useEffect(() => {
    setHasRequestedAdditionalNews(false);
  }, [currentResultCount]);

  return (
    <List
      className="news-entry__article-list"
      ref={listRef}
      onScroll={() => {
        if (getHasScrolledPastThreshold() && hasAdditionalNewsItemsToLoad && !hasRequestedAdditionalNews) {
          dispatch(getNews(newsPage));
          setHasRequestedAdditionalNews(true);
        }
      }}
    >
      {newsPage.result.map(({title, description, publishDate}) => (
        <>
          <ListItem>
            <ListItemText
              primary={
                <div className="news-entry__header">
                  <span className="news-entry__title">{title}</span>
                  <span className="news-entry__published-date">Published Date: {toFormattedDateTime(publishDate)}</span>
                </div>
              }
              secondary={<p className="news-entry__article">{formatDescription(description || '')}</p>}
            />
          </ListItem>
          <Divider />
        </>
      ))}
      {hasAdditionalNewsItemsToLoad && (
        <ListItem>
          <div className="news-entry__loading-more-results-item">
            Loading Additional News &nbsp;
            <CircularProgress />
          </div>
        </ListItem>
      )}
    </List>
  );
};

export default AgentNewsList;
